// PlanetAnimation.jsx
import React, { useRef, useEffect } from 'react';

const PlanetAnimation = () => {
    const canvasRef = useRef(null);
    const rafRef = useRef(null);

    useEffect(() => {
        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d', { alpha: false });
        const planets = [];
        const stars = [];

        // DPR-aware sizing
        const fitCanvas = () => {
            const dpr = Math.max(1, window.devicePixelRatio || 1);
            const { innerWidth: w, innerHeight: h } = window;
            canvas.style.width = `${w}px`;
            canvas.style.height = `${h}px`;
            canvas.width = Math.floor(w * dpr);
            canvas.height = Math.floor(h * dpr);
            ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
        };

        fitCanvas();

        // Stars
        for (let i = 0; i < 60; i++) {
            stars.push({
                x: Math.random() * window.innerWidth,
                y: Math.random() * window.innerHeight,
                radius: 0.5 + Math.random(),
                opacity: 0.3 + Math.random() * 0.4,
            });
        }

        // Background (brand vignette)
        const createBackground = () => {
            const gradient = ctx.createRadialGradient(
                window.innerWidth / 2,
                window.innerHeight / 2,
                0,
                window.innerWidth / 2,
                window.innerHeight / 2,
                Math.max(window.innerWidth, window.innerHeight)
            );
            gradient.addColorStop(0, '#404f6b');   // brand center
            gradient.addColorStop(0.5, '#2f3a52'); // brand dark
            gradient.addColorStop(1, '#000000');   // edges to black
            ctx.fillStyle = gradient;
            ctx.fillRect(0, 0, window.innerWidth, window.innerHeight);
        };

        // Planets
        for (let i = 0; i < 15; i++) {
            planets.push({
                x: Math.random() * window.innerWidth,
                y: Math.random() * window.innerHeight,
                radius: Math.random() * 5 + 1,
                color: ['#C7932C', '#B6E2DB', '#DFDEE5', '#a3aaad'][Math.floor(Math.random() * 4)],
                speed: Math.random() * 0.5 + 0.1,
                angle: Math.random() * Math.PI * 2,
            });
        }

        const animate = () => {
            createBackground();

            // Stars
            stars.forEach((star) => {
                ctx.globalAlpha = star.opacity;
                ctx.fillStyle = '#FFFFFF';
                ctx.beginPath();
                ctx.arc(star.x, star.y, star.radius, 0, Math.PI * 2);
                ctx.fill();
            });

            // Planets
            ctx.globalAlpha = 1;
            planets.forEach((planet) => {
                planet.x += Math.cos(planet.angle) * planet.speed;
                planet.y += Math.sin(planet.angle) * planet.speed;

                if (planet.x > window.innerWidth + planet.radius) planet.x = -planet.radius;
                if (planet.x < -planet.radius) planet.x = window.innerWidth + planet.radius;
                if (planet.y > window.innerHeight + planet.radius) planet.y = -planet.radius;
                if (planet.y < -planet.radius) planet.y = window.innerHeight + planet.radius;

                // Body
                ctx.fillStyle = planet.color;
                ctx.beginPath();
                ctx.arc(planet.x, planet.y, planet.radius, 0, Math.PI * 2);
                ctx.fill();

                // Glow
                const g = ctx.createRadialGradient(
                    planet.x, planet.y, planet.radius,
                    planet.x, planet.y, planet.radius * 2
                );
                g.addColorStop(0, planet.color);
                g.addColorStop(1, 'transparent');
                ctx.fillStyle = g;
                ctx.beginPath();
                ctx.arc(planet.x, planet.y, planet.radius * 2, 0, Math.PI * 2);
                ctx.fill();
            });

            rafRef.current = requestAnimationFrame(animate);
        };

        animate();

        // Debounced resize
        let resizeTO = null;
        const onResize = () => {
            clearTimeout(resizeTO);
            resizeTO = setTimeout(() => {
                fitCanvas();
            }, 100);
        };
        window.addEventListener('resize', onResize);

        return () => {
            window.removeEventListener('resize', onResize);
            if (rafRef.current) cancelAnimationFrame(rafRef.current);
        };
    }, []);

    return (
        <canvas
            ref={canvasRef}
            className="fixed inset-0 w-full h-full -z-10"
            style={{ pointerEvents: 'none' }}
        />
    );
};

export default PlanetAnimation;
